import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_USERS, GET_PROPERTIES, GET_SERVICE_TYPES } from '../../graphql/queries';
import ServiceTypeFilter from '../schedule/filters/ServiceTypeFilter';
import AreaFilter from '../schedule/filters/AreaFilter';
import RoleBasedServiceTypeFilter from '../schedule/filters/RoleBasedServiceTypeFilter';
import DateRangeSelector from './DateRangeSelector';
import JobStatusFilter from './JobStatusFilter';

const AdvancedFiltersSection = ({ 
  searchTerm, 
  setSearchTerm, 
  selectedServiceTypes, 
  onServiceTypeChange, 
  selectedAreas, 
  onAreaChange, 
  userRole,
  isRoleFilterEnabled,
  onRoleFilterToggle,
  resetFilters,
  onApplyFilters,
  isGenerating,
  selectedProperties,
  onSelectedPropertiesChange,
  selectedTeamMembers,
  onSelectedTeamMembersChange,
  // Add date range props
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  // Add job status props
  selectedJobStatuses,
  onJobStatusChange
}) => {
  const [inputValue, setInputValue] = useState(searchTerm);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const searchDropdownRef = useRef(null);

  // Get data for suggestions
  const { data: usersData } = useQuery(GET_USERS);
  const { data: serviceTypesData, loading: serviceTypesLoading } = useQuery(GET_SERVICE_TYPES);

  // Property query for search suggestions
  const propertyQueryVariables = useMemo(() => {
    if (!inputValue || inputValue.length < 2) {
      return null;
    }
    
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    const sixMonthsFromNow = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());
    
    const startDate = sixMonthsAgo.toISOString().split('T')[0];
    const endDate = sixMonthsFromNow.toISOString().split('T')[0];
    
    if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
      return null;
    }
    
    return {
      limit: 20,
      page: 1,
      search: inputValue.trim(),
      startDate,
      endDate
    };
  }, [inputValue]);

  const { data: propertiesData, loading: propertiesLoading, error: propertiesError } = useQuery(GET_PROPERTIES, {
    variables: propertyQueryVariables,
    skip: !propertyQueryVariables,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    onError: (error) => {
      console.error('Error fetching properties:', error);
    }
  });

  // Extract data for suggestions
  const users = useMemo(() => usersData?.users || [], [usersData]);
  const properties = useMemo(() => {
    if (!propertyQueryVariables || propertiesError) {
      return [];
    }
    return propertiesData?.propertiesWithReservations?.properties || [];
  }, [propertiesData, propertyQueryVariables, propertiesError]);

  const availableServiceTypes = useMemo(() => {
    return serviceTypesData?.serviceTypes || [];
  }, [serviceTypesData]);

  // Generate suggestions based on input
  const generateSuggestions = useCallback((value) => {
    if (!value || value.length < 2) {
      return [];
    }
    
    const suggestions = [];
    const lowercaseValue = value.toLowerCase();
    const addedPropertyIds = new Set();
    
    // Add property suggestions
    if (properties && properties.length > 0) {
      properties.forEach(property => {
        if (addedPropertyIds.has(property.id)) return;
        
        if (property.name && property.name.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: property.id,
            text: property.name,
            type: 'property',
            icon: '🏠'
          });
          addedPropertyIds.add(property.id);
        }
      });
      
      properties.forEach(property => {
        if (addedPropertyIds.has(property.id)) return;
        
        if (property.propertyCode && property.propertyCode.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: property.id,
            text: property.name,
            subtext: `Property Code: ${property.propertyCode}`,
            type: 'property',
            icon: '🏠'
          });
          addedPropertyIds.add(property.id);
        }
      });
    }
    
    // Add user suggestions
    users.forEach(user => {
      const fullName = `${user.firstName} ${user.lastName}`;
      if (fullName.toLowerCase().includes(lowercaseValue)) {
        suggestions.push({
          id: user.id,
          text: fullName,
          type: 'team',
          icon: '👤'
        });
      }
    });
    
    // Add service type suggestions
    if (availableServiceTypes) {
      availableServiceTypes.forEach(serviceType => {
        if (serviceType.title.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: serviceType.id,
            text: serviceType.title,
            type: 'service',
            icon: '🔧',
            color: serviceType.color
          });
        }
      });
    }
    
    return suggestions.slice(0, 10);
  }, [properties, users, availableServiceTypes]);

  // Update suggestions when input changes
  useEffect(() => {
    if (inputValue.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    const debounceTimeout = setTimeout(() => {
      const newSuggestions = generateSuggestions(inputValue);
      setSearchSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }, 300);
    
    return () => clearTimeout(debounceTimeout);
  }, [inputValue, generateSuggestions]);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync inputValue with searchTerm when it changes externally
  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);

  // Handle selecting a suggestion
  const handleSelectSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setInputValue(''); // Clear input after selection
    setShowSuggestions(false);

    // Add to appropriate filter list based on type
    if (suggestion.type === 'property') {
      const property = properties.find(p => p.id === suggestion.id);
      if (property && !selectedProperties.find(p => p.id === property.id)) {
        onSelectedPropertiesChange([...selectedProperties, property]);
      }
    } else if (suggestion.type === 'team') {
      const user = users.find(u => u.id === suggestion.id);
      if (user && !selectedTeamMembers.find(u => u.id === user.id)) {
        onSelectedTeamMembersChange([...selectedTeamMembers, user]);
      }
    } else if (suggestion.type === 'service') {
      const serviceType = availableServiceTypes.find(st => st.id === suggestion.id);
      if (serviceType && !selectedServiceTypes.find(st => st === serviceType.id)) {
        onServiceTypeChange([...selectedServiceTypes, serviceType.id]);
      }
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (selectedSuggestion && value !== selectedSuggestion.text) {
      setSelectedSuggestion(null);
    }
    
    setSearchTerm(value);
  };

  // Handle removing selected items
  const handleRemoveProperty = (propertyId) => {
    onSelectedPropertiesChange(selectedProperties.filter(p => p.id !== propertyId));
  };

  const handleRemoveTeamMember = (userId) => {
    onSelectedTeamMembersChange(selectedTeamMembers.filter(u => u.id !== userId));
  };

  return (
    <div className="space-y-6">
      {/* Date Range Section - First Priority */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Date Range</h4>
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Unified Autocomplete Search */}
        <div className="relative" ref={searchDropdownRef}>
          <label htmlFor="advanced-search" className="block text-sm font-medium text-gray-700 mb-1">
            Search & Add Filters
          </label>
          
          <div className="mt-1 relative">
            <input
              type="text"
              id="advanced-search"
              value={inputValue}
              onChange={handleSearchInputChange}
              onClick={() => {
                if (inputValue.length >= 2) {
                  setShowSuggestions(true);
                }
              }}
              placeholder="Search properties, team members, or service types..."
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />
            
            {inputValue && (
              <button
                type="button"
                onClick={() => {
                  setInputValue('');
                  setSearchTerm('');
                  setSelectedSuggestion(null);
                }}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Clear search</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Search dropdown */}
          {showSuggestions && inputValue.length >= 2 && (
            <div 
              className="absolute mt-1 w-full rounded-md bg-white shadow-lg z-50"
              style={{ 
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
              }}
            >
              <ul className="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                {searchSuggestions.length === 0 && (
                  <li className="text-gray-500 select-none relative py-2 pl-3 pr-9">
                    {propertiesLoading 
                      ? "Loading properties..." 
                      : "No matches found. Try a different search term."}
                  </li>
                )}
                
                {searchSuggestions.filter(s => s.type === 'property').length > 0 && (
                  <li className="bg-gray-100 text-gray-600 font-semibold px-3 py-1">
                    Properties ({searchSuggestions.filter(s => s.type === 'property').length})
                  </li>
                )}
                
                {searchSuggestions
                  .filter(s => s.type === 'property')
                  .map(suggestion => (
                    <li
                      key={`${suggestion.id}-${suggestion.type}`}
                      className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-900 font-medium truncate">
                          {suggestion.text}
                        </span>
                      </div>
                      {suggestion.subtext && (
                        <span className="text-gray-500 truncate">
                          {suggestion.subtext}
                        </span>
                      )}
                    </li>
                  ))}

                {searchSuggestions.filter(s => s.type === 'team').length > 0 && (
                  <li className="bg-gray-100 text-gray-600 font-semibold px-3 py-1">
                    Team Members ({searchSuggestions.filter(s => s.type === 'team').length})
                  </li>
                )}
                
                {searchSuggestions
                  .filter(s => s.type === 'team')
                  .map(suggestion => (
                    <li
                      key={`${suggestion.id}-${suggestion.type}`}
                      className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-900 font-medium truncate">
                          {suggestion.text}
                        </span>
                      </div>
                    </li>
                  ))}

                {searchSuggestions.filter(s => s.type === 'service').length > 0 && (
                  <li className="bg-gray-100 text-gray-600 font-semibold px-3 py-1">
                    Service Types ({searchSuggestions.filter(s => s.type === 'service').length})
                  </li>
                )}
                
                {searchSuggestions
                  .filter(s => s.type === 'service')
                  .map(suggestion => (
                    <li
                      key={`${suggestion.id}-${suggestion.type}`}
                      className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-900 font-medium truncate">
                          {suggestion.text}
                        </span>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Selected Items Display */}
          {(selectedProperties.length > 0 || selectedTeamMembers.length > 0) && (
            <div className="mt-2 space-y-2">
              {selectedProperties.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700">Selected Properties:</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedProperties.map(property => (
                      <span
                        key={property.id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {property.name}
                        <button
                          type="button"
                          onClick={() => handleRemoveProperty(property.id)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedTeamMembers.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700">Selected Team Members:</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedTeamMembers.map(user => (
                      <span
                        key={user.id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                      >
                        {user.firstName} {user.lastName}
                        <button
                          type="button"
                          onClick={() => handleRemoveTeamMember(user.id)}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Service Type Filter */}
        <div>
          <RoleBasedServiceTypeFilter
            selectedServiceTypes={selectedServiceTypes}
            onChange={onServiceTypeChange}
            availableServiceTypes={availableServiceTypes}
            loading={serviceTypesLoading}
          />
        </div>
        
        {/* Area Filter */}
        <div>
          <AreaFilter
            selectedAreas={selectedAreas}
            onChange={onAreaChange}
          />
        </div>
        
        {/* Job Status Filter */}
        <div>
          <JobStatusFilter
            selectedStatuses={selectedJobStatuses}
            onChange={onJobStatusChange}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
        >
          Reset Filters
        </button>
        <button
          type="button"
          onClick={onApplyFilters}
          disabled={isGenerating}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? 'Generating...' : 'Generate Report'}
        </button>
      </div>
    </div>
  );
};

export default AdvancedFiltersSection; 